import { ApiProperty } from '@nestjs/swagger';

/**
 * DTO para un KPI individual (valor + variación).
 * Usado para los 4 KPIs principales del dashboard.
 */
export class DashboardKpiDto {
  @ApiProperty({ description: 'Valor actual', example: 364838 })
  valor!: number;

  @ApiProperty({
    description: 'Variación porcentual vs período anterior',
    example: 189,
  })
  variacion!: number;
}

/**
 * DTO para un item de distribución de servicios (gráfico de torta).
 * Muestra tipo de servicio, cantidad, monto y porcentaje del total.
 */
export class DashboardServicioDistribucionDto {
  @ApiProperty({
    description: 'Tipo de servicio',
    example: 'Agenciamiento Contenedor',
  })
  tipoServicio!: string;

  @ApiProperty({ description: 'Cantidad de servicios', example: 2592 })
  cantidad!: number;

  @ApiProperty({ description: 'Monto total en USD', example: 196808000 })
  monto!: number;

  @ApiProperty({ description: 'Porcentaje del total', example: 63.36 })
  porcentaje!: number;
}

/**
 * DTO para un item de distribución de órdenes de servicio agencia tipo "Venta" (gráfico de torta).
 * Muestra nombre del servicio, cantidad de órdenes, monto total y porcentaje.
 */
export class DashboardOsAgenciaVentaDistribucionDto {
  @ApiProperty({
    description: 'Nombre del servicio',
    example: 'Servicio Gate Out',
  })
  serviceName!: string;

  @ApiProperty({ description: 'Cantidad de órdenes de servicio', example: 150 })
  cantidad!: number;

  @ApiProperty({ description: 'Monto total', example: 2250000 })
  montoTotal!: number;

  @ApiProperty({ description: 'Porcentaje del total', example: 25.5 })
  porcentaje!: number;
}

/**
 * DTO para un item de evolución mensual (gráfico de barras).
 * Usado para cada mes en cada una de las 4 métricas.
 */
export class DashboardEvolucionMensualItemDto {
  @ApiProperty({ description: 'Nombre del mes', example: 'JANUARY' })
  mes!: string;

  @ApiProperty({ description: 'Número del mes (1-12)', example: 1 })
  mesNumero!: number;

  @ApiProperty({
    description: 'Valor de la métrica para ese mes',
    example: 158,
  })
  valor!: number;
}

/**
 * DTO de respuesta del Dashboard Servicio Integral.
 *
 * **Estructura**:
 * - `kpis`: 4 KPIs principales (contenedores, pallets, cajas, despachos) con variación vs temporada anterior
 * - `temporada`: Información de la temporada consultada
 * - `distribucionOsAgenciaVenta`: Distribución de órdenes de servicio agencia tipo "Venta" agrupadas por ServiceName
 * - `distribucionOsAgenciaTraspaso`: Distribución de órdenes de servicio agencia tipo "Traspaso" agrupadas por ServiceName
 */
export class DashboardServicioIntegralResponseDto {
  @ApiProperty({
    description:
      'KPIs principales (contenedores, pallets, cajas, despachos) con variación vs temporada anterior',
    example: {
      contenedores: { valor: 364838, variacion: 189 },
      pallets: { valor: 18654114, variacion: 422 },
      cajas: { valor: 8983713, variacion: 46 },
      despachos: { valor: 2628, variacion: 46 },
    },
  })
  kpis!: {
    contenedores: DashboardKpiDto;
    pallets: DashboardKpiDto;
    cajas: DashboardKpiDto;
    despachos: DashboardKpiDto;
  };

  @ApiProperty({
    description: 'Información de la temporada consultada',
    example: {
      temporadaId: 7,
      temporadaNombre: '2024 - 2025',
    },
  })
  temporada!: {
    temporadaId: number;
    temporadaNombre: string;
  };

  @ApiProperty({
    description:
      'Distribución de órdenes de servicio agencia tipo "Venta" agrupadas por ServiceName',
    type: [DashboardOsAgenciaVentaDistribucionDto],
    example: [
      {
        serviceName: 'Servicio Gate Out',
        cantidad: 150,
        montoTotal: 2250000,
        porcentaje: 25.5,
      },
      {
        serviceName: 'Certificado de Origen',
        cantidad: 120,
        montoTotal: 1800000,
        porcentaje: 20.4,
      },
    ],
  })
  distribucionOsAgenciaVenta!: DashboardOsAgenciaVentaDistribucionDto[];

  @ApiProperty({
    description:
      'Distribución de órdenes de servicio agencia tipo "Traspaso" agrupadas por ServiceName',
    type: [DashboardOsAgenciaVentaDistribucionDto],
    example: [
      {
        serviceName: 'Dus Legalizado',
        cantidad: 200,
        montoTotal: 3000000,
        porcentaje: 35.2,
      },
      {
        serviceName: 'Security Fee Contenedor',
        cantidad: 180,
        montoTotal: 2700000,
        porcentaje: 31.7,
      },
    ],
  })
  distribucionOsAgenciaTraspaso!: DashboardOsAgenciaVentaDistribucionDto[];
}
